import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
	AlertTriangle,
	ArrowLeft,
	ChevronRight,
	ShieldAlert,
} from "lucide-react"
import { type ReactNode, useState } from "react"
import { PageHeader, Panel } from "@/components/layout"
import {
	AdminChatProvider,
	AdminChatWidget,
	AdminNotesPanel,
	DevToolsTab,
	DisputeSection,
	InsuranceSection,
	ShieldClaimDocumentsSection,
	OrderItemsTable,
	OrderPageHeader,
	OrderRail,
	RatingsSection,
	RiskIndicatorsCard,
	RiskLevelBadge,
	ShippingSection,
} from "@/components/marketplace"
import { JsonViewCard } from "@/components/marketplace/json-view-card"
import { ModerationReferenceBanner } from "@/components/moderation"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { createTabSearch } from "@/hooks/use-url-tab"
import { useOrderChangeStream } from "@/queries/change-streams/use-order-change-stream"
import { useAdminNotes, useAdminOrderDetail } from "@/queries/marketplace"
import type { AdminOrderDetail } from "@/types/marketplace.types"
import { cn } from "@/utils/styles.helpers"

/** Dev tools tab is hidden in production */
const isProduction =
	typeof window !== "undefined" &&
	window.location.hostname === "admin.cardnexus.com"

const TABS = [
	"details",
	"risk",
	"order-chat",
	"buyer-support",
	"seller-support",
	"full-json",
	"dev-tools",
] as const
type OrderTab = (typeof TABS)[number]

export const Route = createFileRoute("/orders/$orderId/")({
	component: OrderDetailPage,
	validateSearch: createTabSearch(TABS),
})

// Get Stream Chat API key from env
const STREAM_CHAT_API_KEY = import.meta.env.VITE_STREAM_CHAT_API_KEY || ""

/** The unread marker of a support chat tab. */
function UnreadDot() {
	return (
		<span
			role="img"
			aria-label="Unread messages"
			className="size-[7px] rounded-full bg-info"
		/>
	)
}

/** A chat tab's bordered container: the chat at full height, or one muted line when it cannot load. */
function ChatPanel({ children }: { children: ReactNode }) {
	return (
		<Panel className="h-[calc(100dvh-16rem)] min-h-[400px]">{children}</Panel>
	)
}

function ChatUnavailable({ children }: { children: ReactNode }) {
	return <p className="p-4 text-muted-foreground">{children}</p>
}

function OrderDetailPage() {
	const { orderId } = Route.useParams()
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const { order, isLoading, error } = useAdminOrderDetail({ orderId })
	useOrderChangeStream({ id: order?.id, queryKeyId: orderId })
	const requestedTab = searchParams.tab ?? "details"
	const activeTab =
		requestedTab === "dev-tools" && isProduction ? "details" : requestedTab

	const setActiveTab = (tab: string) => {
		navigate({
			to: "/orders/$orderId",
			params: { orderId },
			search: tab === "details" ? {} : { tab: tab as OrderTab },
			replace: true,
		})
	}
	const [notesOpen, setNotesOpen] = useState(false)
	const { total: notesTotal } = useAdminNotes({
		targetType: "order",
		targetId: order?.id ?? "",
		enabled: !!order,
	})

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<div className="flex flex-col gap-2">
					<Skeleton className="h-7 w-80" />
					<Skeleton className="h-5 w-[28rem]" />
				</div>
				<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
					<div className="flex flex-col gap-3">
						<Skeleton className="h-9 w-full" />
						<Skeleton className="h-9 w-full" />
						<Skeleton className="h-9 w-full" />
						<Skeleton className="h-9 w-full" />
					</div>
					<div className="flex flex-col gap-3">
						<Skeleton className="h-32" />
						<Skeleton className="h-64" />
					</div>
				</div>
			</div>
		)
	}

	if (error || !order) {
		return (
			<div className="p-4 sm:p-6">
				<PageHeader
					title="Order not found"
					description={
						error?.message || `The order with ID ${orderId} could not be found.`
					}
					actions={
						<Button variant="outline" asChild>
							<Link to="/orders" search={{} as never}>
								<ArrowLeft />
								Back to orders
							</Link>
						</Button>
					}
				/>
			</div>
		)
	}

	const showNotes = notesOpen || (notesTotal ?? 0) > 0

	const handleAddNote = () => {
		setNotesOpen(true)
		setActiveTab("details")
		setTimeout(() => {
			document
				.getElementById("admin-notes")
				?.scrollIntoView({ behavior: "smooth", block: "center" })
		}, 50)
	}

	return (
		<div className="px-4 pb-10 sm:px-6">
			<OrderPageHeader order={order} onAddNote={handleAddNote} />

			<ModerationReferenceBanner
				reference={{ orderId: order.id }}
				className="mt-6"
			/>

			<div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
				<div className="min-w-0">
					<Tabs
						value={activeTab}
						onValueChange={setActiveTab}
						className="gap-6"
					>
						<TabsList variant="line">
							<TabsTrigger value="details">Details</TabsTrigger>
							<TabsTrigger value="risk">
								Risk
								<RiskLevelBadge
									riskLevel={order.riskLevel}
									count={order.riskIndicators?.length}
								/>
							</TabsTrigger>
							<TabsTrigger value="order-chat">Order chat</TabsTrigger>
							<TabsTrigger value="buyer-support">
								Buyer support
								{order.supportBuyerHasUnread && <UnreadDot />}
							</TabsTrigger>
							<TabsTrigger value="seller-support">
								Seller support
								{order.supportSellerHasUnread && <UnreadDot />}
							</TabsTrigger>
							<TabsTrigger value="full-json">Full JSON</TabsTrigger>
							{!isProduction && (
								<TabsTrigger value="dev-tools">Dev tools</TabsTrigger>
							)}
						</TabsList>

						<TabsContent value="details" className="mt-0">
							<div className="flex min-w-0 flex-col gap-4">
								<RiskReviewCallout
									order={order}
									onViewRisk={() => setActiveTab("risk")}
								/>
								{order.dispute && <DisputeSection order={order} />}
								{showNotes && (
									<div id="admin-notes" className="scroll-mt-28">
										<AdminNotesPanel
											targetType="order"
											targetId={order.id}
											variant="panel"
										/>
									</div>
								)}
								<OrderItemsTable order={order} />
								<ShippingSection order={order} />
								{order.shipping?.insurance?.optedIn && (
									<InsuranceSection order={order} />
								)}
								{order.shipping?.insurance?.figural && (
									<ShieldClaimDocumentsSection orderId={order.id} />
								)}
								<RatingsSection order={order} />
							</div>
						</TabsContent>

						<TabsContent value="risk" className="mt-0">
							<RiskIndicatorsCard order={order} />
						</TabsContent>

						{/* Admin can post as CardNexus Support */}
						<TabsContent value="order-chat" className="mt-0">
							<ChatPanel>
								{STREAM_CHAT_API_KEY && activeTab === "order-chat" ? (
									<AdminChatProvider
										apiKey={STREAM_CHAT_API_KEY}
										orderId={order.id}
										channelType="main"
									>
										<AdminChatWidget
											channelId={order.mainChannelId}
											participantName={`@${order.buyer.username} ↔ @${order.seller.username}`}
										/>
									</AdminChatProvider>
								) : (
									<ChatUnavailable>Chat API key not configured</ChatUnavailable>
								)}
							</ChatPanel>
						</TabsContent>

						<TabsContent value="buyer-support" className="mt-0">
							<ChatPanel>
								{STREAM_CHAT_API_KEY && activeTab === "buyer-support" ? (
									<AdminChatProvider
										apiKey={STREAM_CHAT_API_KEY}
										orderId={order.id}
										channelType="support-buyer"
									>
										<AdminChatWidget
											channelId={order.buyerSupportChannelId}
											participantName={`@${order.buyer.username}`}
										/>
									</AdminChatProvider>
								) : (
									<ChatUnavailable>
										{!STREAM_CHAT_API_KEY
											? "Chat API key not configured"
											: "Switch to this tab to load chat"}
									</ChatUnavailable>
								)}
							</ChatPanel>
						</TabsContent>

						<TabsContent value="seller-support" className="mt-0">
							<ChatPanel>
								{STREAM_CHAT_API_KEY && activeTab === "seller-support" ? (
									<AdminChatProvider
										apiKey={STREAM_CHAT_API_KEY}
										orderId={order.id}
										channelType="support-seller"
									>
										<AdminChatWidget
											channelId={order.sellerSupportChannelId}
											participantName={`@${order.seller.username}`}
										/>
									</AdminChatProvider>
								) : (
									<ChatUnavailable>
										{!STREAM_CHAT_API_KEY
											? "Chat API key not configured"
											: "Switch to this tab to load chat"}
									</ChatUnavailable>
								)}
							</ChatPanel>
						</TabsContent>

						<TabsContent value="full-json" className="mt-0">
							<JsonViewCard
								title="Full order JSON"
								description="Raw order data for debugging"
								data={order}
							/>
						</TabsContent>

						{!isProduction && (
							<TabsContent value="dev-tools" className="mt-0">
								<DevToolsTab order={order} />
							</TabsContent>
						)}
					</Tabs>
				</div>

				<OrderRail order={order} />
			</div>
		</div>
	)
}

/**
 * A tinted row above the details when a warning or critical order still
 * needs its risk review; opens the Risk tab.
 */
function RiskReviewCallout({
	order,
	onViewRisk,
}: {
	order: AdminOrderDetail
	onViewRisk: () => void
}) {
	if (!order.needsReview) return null
	if (order.riskLevel !== "warning" && order.riskLevel !== "critical")
		return null

	const indicators = order.riskIndicators ?? []
	const criticalCount = indicators.filter(
		(i) => i.severity === "critical",
	).length
	const warningCount = indicators.filter((i) => i.severity === "warning").length

	const isCritical = order.riskLevel === "critical"
	const Icon = isCritical ? ShieldAlert : AlertTriangle

	return (
		<button
			type="button"
			onClick={onViewRisk}
			className={cn(
				"flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors",
				isCritical
					? "bg-bad-soft hover:bg-bad-soft/80"
					: "bg-warn-soft hover:bg-warn-soft/80",
			)}
		>
			<Icon
				className={cn("size-4 shrink-0", isCritical ? "text-bad" : "text-warn")}
			/>
			<div className="flex min-w-0 flex-1 flex-col">
				<span
					className={cn("font-medium", isCritical ? "text-bad" : "text-warn")}
				>
					This order needs to be reviewed
				</span>
				<span className="text-xs text-muted-foreground">
					{[
						criticalCount > 0 && `${criticalCount} critical`,
						warningCount > 0 && `${warningCount} warning`,
					]
						.filter(Boolean)
						.join(", ")}{" "}
					signal{criticalCount + warningCount !== 1 ? "s" : ""}
				</span>
			</div>
			<span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
				View risk
				<ChevronRight className="size-3.5" />
			</span>
		</button>
	)
}
